import { createRouter, createWebHistory } from "vue-router";
import AppLayout from "@/layout/AppLayout.vue";
import { useAuthStore } from "@/stores/auth";
import { useSystemContextStore } from "@/stores/system-context";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/login",
      name: "login",
      component: () => import("@/views/auth/LoginView.vue"),
      meta: { title: "登录", public: true },
    },
    {
      path: "/",
      component: AppLayout,
      redirect: "/dashboard",
      children: [
        {
          path: "dashboard",
          name: "dashboard",
          component: () => import("@/views/dashboard/DashboardView.vue"),
          meta: { title: "工作台", eyebrow: "OVERVIEW" },
        },
        {
          path: "work-items",
          name: "my-work",
          component: () => import("@/views/work-items/MyWorkView.vue"),
          meta: { title: "我的工作", eyebrow: "MY WORK" },
        },
        {
          path: "todo-center",
          name: "todo-center",
          redirect: "/work-items",
        },
        {
          path: "projects",
          name: "projects",
          component: () => import("@/views/projects/ProjectsView.vue"),
          meta: {
            title: "项目管理",
            description: "维护项目主数据、负责人、成员和授权范围。",
          },
        },
        {
          path: "projects/:id",
          name: "project-detail",
          component: () => import("@/views/projects/ProjectDetailView.vue"),
          meta: { title: "项目详情" },
        },
        {
          path: "crm",
          name: "crm",
          component: () => import("@/views/customers/CustomersView.vue"),
          meta: {
            title: "客户经营",
            eyebrow: "CUSTOMER OPERATIONS",
            permission: "customer:view",
            feature: "crm",
          },
        },
        {
          path: "customers/:id",
          name: "customer-detail",
          component: () => import("@/views/customers/CustomerDetailView.vue"),
          meta: {
            title: "客户详情",
            permission: "customer:view",
            feature: "crm",
          },
        },
        {
          path: "meetings",
          name: "meetings",
          component: () => import("@/views/meetings/MeetingsView.vue"),
          meta: {
            title: "会议与沟通",
            eyebrow: "CUSTOMER COMMUNICATION",
            permission: "customer:view",
            feature: "crm",
          },
        },
        {
          path: "meetings/:id",
          name: "meeting-detail",
          component: () => import("@/views/meetings/MeetingDetailView.vue"),
          meta: {
            title: "会议详情",
            permission: "customer:view",
            feature: "crm",
          },
        },
        {
          path: "issues",
          name: "issues",
          component: () => import("@/views/issues/IssuesView.vue"),
          meta: { title: "问题管理", eyebrow: "ISSUE REGISTER" },
        },
        {
          path: "matters",
          name: "matters",
          component: () => import("@/views/matters/MattersView.vue"),
          meta: { title: "专题与关系", eyebrow: "TOPICS & RELATIONSHIPS" },
        },
        {
          path: "intake",
          name: "intake",
          component: () =>
            import("@/views/dingtalk-intake/DingTalkIntakeView.vue"),
          meta: {
            title: "信息准入中心",
            eyebrow: "INFORMATION INTAKE",
            permission: "issue:create",
          },
        },
        {
          path: "dingtalk-intake",
          redirect: "/intake",
        },
        {
          path: "release-versions",
          name: "release-versions",
          component: () =>
            import("@/views/release-versions/ReleaseVersionsView.vue"),
          meta: {
            title: "发布版本管理",
            eyebrow: "RELEASE REGISTER",
            permission: "release_version:view",
          },
        },
        {
          path: "release-versions/:id",
          name: "release-version-detail",
          component: () =>
            import("@/views/release-versions/ReleaseVersionDetailView.vue"),
          meta: { title: "发布版本详情", permission: "release_version:view" },
        },
        {
          path: "issues/new",
          name: "issue-create",
          component: () => import("@/views/issues/IssueFormView.vue"),
          meta: { title: "新增问题", permission: "issue:create" },
        },
        {
          path: "issues/imports",
          name: "issue-imports",
          component: () => import("@/views/imports/ImportsView.vue"),
          meta: {
            title: "问题批量导入",
            description: "预校验、处理冲突并追踪正式问题导入批次。",
            permission: "import:manage",
          },
        },
        {
          path: "issues/:id",
          name: "issue-detail",
          component: () => import("@/views/issues/IssueDetailView.vue"),
          meta: { title: "问题详情" },
        },
        {
          path: "imports",
          redirect: (to) => ({ path: "/issues/imports", query: to.query }),
        },
        {
          path: "ai",
          name: "ai",
          component: () => import("@/views/ai/AiView.vue"),
          meta: {
            title: "AI分析中心",
            description: "集中查看AI摘要、风险建议及人工确认结果。",
            permission: "ai:analyze",
          },
        },
        {
          path: "ai/search",
          name: "unified-search",
          component: () => import("@/views/ai/UnifiedSearchView.vue"),
          meta: {
            title: "智能检索",
            description: "统一检索项目、问题、信息准入和会议证据。",
            permission: "ai:analyze",
          },
        },
        {
          path: "reports",
          name: "reports",
          component: () => import("@/views/reports/ReportsView.vue"),
          meta: { title: "工作报告", permission: "report:view" },
        },
        {
          path: "reports/personal/:id",
          name: "personal-report-editor",
          component: () => import("@/views/reports/PersonalReportEditorView.vue"),
          meta: { title: "报告工作台", permission: "report:view" },
        },
        {
          path: "notifications",
          name: "notifications",
          component: () =>
            import("@/views/notifications/NotificationsView.vue"),
          meta: { title: "提醒中心" },
        },
        {
          path: "system",
          name: "system",
          component: () => import("@/views/system/SystemView.vue"),
          meta: { title: "系统管理", permission: "system:manage" },
        },
      ],
    },
  ],
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (to.meta.public)
    return auth.authenticated && to.path === "/login" ? "/dashboard" : true;
  if (!auth.initialized) {
    await auth.load().catch(() => null);
  }
  if (!auth.authenticated)
    return { path: "/login", query: { redirect: to.fullPath } };
  try {
    await auth.load();
  } catch {
    return { path: "/login", query: { redirect: to.fullPath } };
  }
  const permission = to.meta.permission as string | undefined;
  if (permission && !auth.can(permission)) return "/dashboard";
  const feature = to.meta.feature as "crm" | undefined;
  if (feature) {
    const systemContext = useSystemContextStore();
    await systemContext.load();
    if (!systemContext.features[feature]) return "/dashboard";
  }
  return true;
});

router.afterEach((to) => {
  document.title = `${String(to.meta.title ?? "工作台")} - AI工作平台`;
});

export default router;
